import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Search, Edit, Eye, Award, Plus, CheckCheck, Check, Ban } from "lucide-react"
import { useAllFormats, TournamentFormat } from "@/graphql/hooks/useAllFormats"
import { Skeleton } from "@/components/ui/skeleton"
import { useAllGames } from "@/graphql"
import { FormatEditModal } from "./FormatEditModal"

export function FormatsManagement() {
  const [searchTerm, setSearchTerm] = useState("")
  const [editingFormat, setEditingFormat] = useState<TournamentFormat | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { formats, loading, error, refetch } = useAllFormats()
  const { gamesMap, loading: loadingGames } = useAllGames(false)

  const filteredFormats =
    formats?.filter((format) => format.name.toLowerCase().includes(searchTerm.toLowerCase())) || []

  if (loading || loadingGames) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error cargando formatos: {error.message}
            <Button onClick={() => refetch()} className="ml-4" variant="outline">
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar formatos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Formato
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Formatos de Juego ({filteredFormats.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Juegos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Torneos</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFormats.map((format) => (
                <TableRow key={format.id}>
                  <TableCell className="font-medium">{format.name}</TableCell>
                  <TableCell className="max-w-xs truncate">Sin descripción</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {format.FormatGames?.map((fg) => {
                        const game = gamesMap.get(fg.gameId)
                        return (
                          <Badge
                            key={fg.gameId}
                            variant={game.isActive ? "secondary" : "outline"}
                            className="text-xs cursor-pointer"
                          >
                            {game.isActive ? (
                              <Check className="h-3 w-3 mr-1" />
                            ) : (
                              <Ban className="h-3 w-3 mr-1" />
                            )}
                            {game.name}
                          </Badge>
                        )
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={format.status ? "default" : "secondary"}>
                      {format.status ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell>{format.totalTournaments}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingFormat(format)
                          setIsEditModalOpen(true)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FormatEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        format={editingFormat}
        onSuccess={refetch}
      />
    </div>
  )
}
